import { Project, Schedule } from '../types/project';
import { API_BASE_URL } from '../config';
import { getAuthHeaders } from './workosAuth';
import { authFetch } from './config';

// API response types
// interface ApiResponse<T> {
//   data: T;
//   message?: string;
//   error?: string;
// }

interface CreateProjectRequest {
  name: string;
  description?: string;
  organization_id: string;
  status?: string;
  created_by: string;
}

interface CreateScheduleRequest {
  name: string;
  description?: string;
  status: string;
  project_id: string;
  startDate?: string;
  endDate?: string;
  duration?: number;
  activityCount?: number;
}

// Project API functions
export const projectApi = {
  // Get all projects (supports legacy array and v1 { items } format; uses auth when available)
  async getProjects(options?: { status?: 'active' | 'archived' | 'all' }): Promise<Project[]> {
    try {
      const params = new URLSearchParams();
      if (options?.status) params.set('status', options.status);
      const qs = params.toString();
      const response = await authFetch(`${API_BASE_URL}/projects${qs ? `?${qs}` : ''}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (Array.isArray(data)) return data;
      if (data?.items && Array.isArray(data.items)) return data.items;
      if (data?.projects && Array.isArray(data.projects)) return data.projects;
      if (data?.data && Array.isArray(data.data)) return data.data;
      return [];
    } catch (error) {
      console.error('Error fetching projects:', error);
      return [];
    }
  },

  /** Get project logo URL if one is uploaded (primary). Returns null on 404 or error. */
  async getProjectLogo(projectId: string): Promise<{ url: string; filename: string } | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/projects/${encodeURIComponent(projectId)}/logo`, {
        credentials: 'include',
        headers: await getAuthHeaders(),
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data?.url != null ? { url: data.url, filename: data.filename ?? '' } : null;
    } catch {
      return null;
    }
  },

  /** Get primary and secondary logo URLs for a project. Missing slots are null. */
  async getProjectLogos(
    projectId: string
  ): Promise<{ primary: { url: string; filename: string } | null; secondary: { url: string; filename: string } | null }> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/projects/${encodeURIComponent(projectId)}/logos`, {
        credentials: 'include',
        headers: await getAuthHeaders(),
      });
      if (!response.ok) return { primary: null, secondary: null };
      const data = await response.json();
      return {
        primary: data?.primary?.url != null ? { url: data.primary.url, filename: data.primary.filename ?? '' } : null,
        secondary: data?.secondary?.url != null ? { url: data.secondary.url, filename: data.secondary.filename ?? '' } : null,
      };
    } catch {
      return { primary: null, secondary: null };
    }
  },

  // Get a single project by ID
  async getProject(id: string): Promise<Project | null> {
    try {
      const response = await authFetch(`${API_BASE_URL}/projects/${id}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Raw project data from API:', data);
      return data;
    } catch (error) {
      console.error('Error fetching project:', error);
      return null;
    }
  },

  // Create a new project
  async createProject(projectData: CreateProjectRequest): Promise<Project> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/projects`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.project || data;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  // Update a project
  async updateProject(id: string, projectData: Partial<CreateProjectRequest>): Promise<Project> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/projects/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.project || data;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  // Delete a project
  async deleteProject(id: string): Promise<boolean> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/projects/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting project:', error);
      return false;
    }
  },

  // Get schedules for a project (uses /api/projects/{id}/schedules — snapshots)
  async getProjectSchedules(projectId: string): Promise<Schedule[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}/schedules`, {
        credentials: 'include',
        headers,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Raw schedule data from API:', data); // Debug log
      
      // Handle the backend response format: { status: "success", data: [...] }
      if (data && data.status === 'success' && Array.isArray(data.data)) {
        console.log('Using data.data format:', data.data); // Debug log
        return data.data;
      }
      
      // Fallback for other formats
      if (Array.isArray(data)) {
        console.log('Using direct array format:', data); // Debug log
        return data;
      }
      
      console.warn('Unexpected schedule data format:', data);
      return [];
    } catch (error) {
      console.error('Error fetching project schedules:', error);
      
      // Fallback mock data for testing when backend is not available
      console.log('Using fallback mock data for testing');
      return [
        {
          id: "mock-schedule-1",
          name: "Schedule from 4418FP-23-24-BL6-EOT24RF-12A (R0) V20.12.xer",
          description: "Mock schedule for testing",
          version: "1.0",
          status: "ready",
          activity_count: 1814,
          relationship_count: 3728,
          start_date: "2020-12-02 00:00:00",
          end_date: "2025-07-20T00:00:00",
          data_date: "2023-12-17 00:00:00",
          duration: 1691,
          xer_project_id: "f8921eb2-dba1-4caf-ac66-db91994fbae0",
          upload_id: "2d5f59c3-2fe9-4e34-8ea0-1bcbacf9ee86",
          created_at: "2025-06-26T23:24:54.121390",
          updated_at: "2025-06-26T23:25:00.285155",
          schedule_metadata: null,
          project_id: projectId
        },
        {
          id: "mock-schedule-2",
          name: "Schedule from U22025-1300 250405.xer",
          description: "Mock schedule for testing",
          version: "1.0",
          status: "ready",
          activity_count: 3045,
          relationship_count: 3491,
          start_date: "2025-01-29 00:00:00",
          end_date: "2025-05-27T00:00:00",
          data_date: "2025-03-01 00:00:00",
          duration: 118,
          xer_project_id: "8a98640f-a61c-47d6-8485-c7fbe1d07f6c",
          upload_id: "99d2b552-f347-4ea2-92ed-c063f8101565",
          created_at: "2025-06-26T23:26:48.317126",
          updated_at: "2025-06-26T23:26:51.010144",
          schedule_metadata: null,
          project_id: projectId
        }
      ];
    }
  },

  // Create a new schedule for a project
  async createSchedule(scheduleData: CreateScheduleRequest): Promise<Schedule> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/projects/${scheduleData.project_id}/schedules`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.schedule || data;
    } catch (error) {
      console.error('Error creating schedule:', error);
      throw error;
    }
  },

  // Upload XER file and create schedule (legacy signature; prefers uploadXerNewProject for new projects)
  async uploadXerFile(projectId: string, file: File, scheduleData: Partial<CreateScheduleRequest>): Promise<Schedule> {
    const res = await this.uploadXerNewProject(scheduleData.name || 'Uploaded Project', file, {
      description: scheduleData.description,
      projectId: projectId === 'auto' ? undefined : projectId,
    });
    return {
      id: res.project_id,
      name: res.project_name,
      description: res.project_description || '',
      version: '1.0',
      status: 'ready',
      activity_count: 0,
      relationship_count: 0,
      start_date: new Date().toISOString(),
      end_date: new Date().toISOString(),
      data_date: new Date().toISOString(),
      duration: 0,
      xer_project_id: res.project_id,
      upload_id: res.import_run_id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      schedule_metadata: null,
      project_id: projectId,
    };
  },

  /**
   * Create a new project from a XER file (or add a new schedule to an existing project).
   * Backend: POST /api/upload/xer
   */
  async uploadXerNewProject(
    projectName: string,
    file: File,
    options?: {
      description?: string;
      organization?: string;
      projectId?: string;
      isBaseline?: boolean;
    }
  ): Promise<{
    success: boolean;
    project_id: string;
    project_name: string;
    import_run_id: string;
    message?: string;
    project_description?: string;
  }> {
    const formData = new FormData();
    formData.append('project_name', projectName);
    formData.append('project_description', options?.description ?? '');
    formData.append('project_organization', options?.organization ?? '');
    formData.append('file', file);
    if (options?.projectId) formData.append('project_id', options.projectId);
    if (options?.isBaseline) formData.append('is_baseline', 'true');

    const response = await authFetch(`${API_BASE_URL}/upload/xer`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const msg = err.detail || (typeof err.detail === 'string' ? err.detail : `Upload failed: ${response.status}`);
      throw new Error(Array.isArray(msg) ? msg[0]?.msg || String(msg) : msg);
    }

    const data = await response.json();
    const idForNav = data.project_id || data.import_run_id || '';
    return {
      success: data.success !== false,
      project_id: idForNav,
      project_name: data.project_name || projectName,
      import_run_id: data.import_run_id || idForNav || '',
      message: data.message,
      project_description: data.project_description,
    };
  },
};

/** Get project overview from v1 API (supports project_id as import_id or proj_id). */
export async function getProjectOverview(projectId: string): Promise<any | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/v1/projects/${encodeURIComponent(projectId)}/overview`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.error('Error fetching project overview:', e);
    return null;
  }
}

// Export individual functions for backward compatibility
export const getProjects = projectApi.getProjects;
export const getProject = projectApi.getProject;
export const createProject = projectApi.createProject;
export const updateProject = projectApi.updateProject;
export const deleteProject = projectApi.deleteProject;
export const getProjectSchedules = projectApi.getProjectSchedules;
export const createSchedule = projectApi.createSchedule;
export const uploadXerFile = projectApi.uploadXerFile;
/** Project summary from v1 API (supports project_id as import_id or proj_id). */
export async function getProjectSummary(projectId: string): Promise<any | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/v1/projects/${encodeURIComponent(projectId)}/summary`, {
      credentials: 'include',
      headers: await getAuthHeaders(),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.error('Error fetching project summary:', e);
    return null;
  }
}